import React, { useCallback, useEffect, useRef } from "react";
import { WidgetWrapper } from "../../../../components/WidgetWrapper/WidgetWrapper";
import { EditField } from "../../../../components/EditPanel/types";
import {
  ConciergePanelWidgetProps,
  ConciergePanelWidgetState,
  ConciergePanelPreset,
} from "./types";
import { useWidgetEditable } from "../../../../hooks/useWidgetEditable";
import { useWidgetEdit } from "../../../../hooks/useWidgetTheme";
import { themeColor, customColor } from "../../../../types/color";
import { WidgetEditSuppressProvider } from "../../../../utils/WidgetEditSuppressContext";

// ============================================================================
// Preset definitions
// ============================================================================

/**
 * Each preset defines styling that is pushed to child widgets:
 *   - suggestionList: pushed to SuggestionListWidget
 *   - chatInput: pushed to ChatInput widget
 *   - panel: applied to this widget's own state (background)
 */
interface PresetConfig {
  panel: Partial<ConciergePanelWidgetState>;
  suggestionList: Record<string, any>;
  chatInput: Record<string, any>;
}

const presetConfigs: Record<
  Exclude<ConciergePanelPreset, "custom">,
  PresetConfig
> = {
  "vertical-compact": {
    panel: {
      mainBgColor: themeColor("transparent"),
      pillLayout: "vertical",
      pillShowIcon: false,
      pillBorderRadius: 8,
      pillBorderColor: themeColor("borderColor"),
      pillBorderWidth: 1,
      pillBgColor: themeColor("secondary"),
      pillTextColor: themeColor("textPrimary"),
      pillContainerBgColor: themeColor("transparent"),
      pillContainerBorderRadius: 0,
      inputBorderColor: customColor("#F3F3F3"),
      inputBorderWidth: 1,
      inputBorderRadius: 100,
      inputMinHeight: 56,
    },
    suggestionList: {
      layout: "vertical",
      showIcon: false,
      containerBgColor: themeColor("transparent"),
      containerPaddingX: 0,
      containerPaddingY: 0,
      containerBorderRadius: 0,
      pillBorderRadius: 8,
      pillBorderColor: themeColor("borderColor"),
      pillBorderWidth: 1,
      pillBgColor: themeColor("secondary"),
      pillBgColorHover: themeColor("background"),
      pillTextColor: themeColor("textPrimary"),
    },
    chatInput: {
      fontSize: 14,
      iconSize: 20,
      iconGap: 6,
      paddingX: 12,
      paddingY: 12,
      borderColor: customColor("#F3F3F3"),
      borderColorFocus: themeColor("primary"),
      borderWidth: 1,
      borderRadiusSingleLine: 100,
      borderRadiusMultiLine: 15,
      minHeight: 56,
    },
  },

  "vertical-expanded": {
    panel: {
      mainBgColor: themeColor("transparent"),
      pillLayout: "vertical",
      pillShowIcon: false,
      pillBorderRadius: 100,
      pillBorderColor: customColor("#F3F3F3"),
      pillBorderWidth: 1,
      pillShadowEnabled: true,
      pillShadow: "0 4px 16px 0 rgba(0, 0, 0, 0.05)",
      pillBgColor: themeColor("secondary"),
      pillTextColor: themeColor("textPrimary"),
      pillContainerBgColor: themeColor("transparent"),
      pillContainerBorderRadius: 12,
      inputBorderColor: customColor("#F3F3F3"),
      inputBorderWidth: 1,
      inputBorderRadius: 20,
      inputMinHeight: 116,
    },
    suggestionList: {
      layout: "vertical",
      showIcon: false,
      containerBgColor: themeColor("transparent"),
      containerPaddingX: 12,
      containerPaddingY: 12,
      containerBorderRadius: 12,
      pillBorderRadius: 100,
      pillBorderColor: customColor("#F3F3F3"),
      pillBorderWidth: 1,
      pillShadowEnabled: true,
      pillShadow: "0 4px 16px 0 rgba(0, 0, 0, 0.05)",
      pillBgColor: themeColor("secondary"),
      pillBgColorHover: themeColor("background"),
      pillTextColor: themeColor("textPrimary"),
    },
    chatInput: {
      fontSize: 14,
      iconSize: 20,
      iconGap: 6,
      paddingX: 12,
      paddingY: 12,
      borderColor: customColor("#F3F3F3"),
      borderColorFocus: themeColor("primary"),
      borderWidth: 1,
      borderRadiusSingleLine: 20,
      borderRadiusMultiLine: 15,
      minHeight: 116,
    },
  },

  "horizontal-compact": {
    panel: {
      mainBgColor: themeColor("transparent"),
      pillLayout: "horizontal",
      pillShowIcon: false,
      pillBorderRadius: 100,
      pillBorderColor: themeColor("borderColor"),
      pillBorderWidth: 1,
      pillBgColor: themeColor("secondary"),
      pillTextColor: themeColor("textPrimary"),
      pillContainerBgColor: themeColor("transparent"),
      pillContainerBorderRadius: 0,
      inputBorderColor: customColor("#F3F3F3"),
      inputBorderWidth: 1,
      inputBorderRadius: 100,
      inputMinHeight: 56,
    },
    suggestionList: {
      layout: "horizontal",
      showIcon: false,
      containerBgColor: themeColor("transparent"),
      containerPaddingX: 0,
      containerPaddingY: 0,
      containerBorderRadius: 0,
      pillPaddingX: 16,
      pillPaddingY: 10,
      pillBorderRadius: 100,
      pillBorderColor: themeColor("borderColor"),
      pillBorderWidth: 1,
      pillBgColor: themeColor("secondary"),
      pillBgColorHover: themeColor("background"),
      pillTextColor: themeColor("textPrimary"),
    },
    chatInput: {
      fontSize: 14,
      iconSize: 20,
      iconGap: 6,
      paddingX: 12,
      paddingY: 12,
      borderColor: customColor("#F3F3F3"),
      borderColorFocus: themeColor("primary"),
      borderWidth: 1,
      borderRadiusSingleLine: 100,
      borderRadiusMultiLine: 15,
      minHeight: 56,
    },
  },

  "horizontal-expanded": {
    panel: {
      mainBgColor: themeColor("transparent"),
      pillLayout: "horizontal",
      pillShowIcon: false,
      pillBorderRadius: 100,
      pillBorderColor: themeColor("borderColor"),
      pillBorderWidth: 1,
      pillBgColor: themeColor("secondary"),
      pillTextColor: themeColor("textPrimary"),
      pillContainerBgColor: themeColor("transparent"),
      pillContainerBorderRadius: 0,
      inputBorderColor: customColor("#F3F3F3"),
      inputBorderWidth: 1,
      inputBorderRadius: 20,
      inputMinHeight: 116,
    },
    suggestionList: {
      layout: "horizontal",
      showIcon: false,
      containerBgColor: themeColor("transparent"),
      containerPaddingX: 0,
      containerPaddingY: 0,
      containerBorderRadius: 0,
      pillPaddingX: 16,
      pillPaddingY: 10,
      pillBorderRadius: 100,
      pillBorderColor: themeColor("borderColor"),
      pillBorderWidth: 1,
      pillBgColor: themeColor("secondary"),
      pillBgColorHover: themeColor("background"),
      pillTextColor: themeColor("textPrimary"),
    },
    chatInput: {
      fontSize: 14,
      iconSize: 20,
      iconGap: 6,
      paddingX: 12,
      paddingY: 12,
      borderColor: customColor("#F3F3F3"),
      borderColorFocus: themeColor("primary"),
      borderWidth: 1,
      borderRadiusSingleLine: 20,
      borderRadiusMultiLine: 15,
      minHeight: 116,
    },
  },
};

// ============================================================================
// Edit panel configuration
// ============================================================================

const PRESET_OPTIONS = [
  { label: "Vertical + Compact Input", value: "vertical-compact" },
  { label: "Vertical + Expanded Input", value: "vertical-expanded" },
  { label: "Horizontal + Compact Input", value: "horizontal-compact" },
  { label: "Horizontal + Expanded Input", value: "horizontal-expanded" },
  { label: "Custom", value: "custom" },
];

const editFields: EditField[] = [
  // --- General ---
  {
    key: "preset",
    label: "Preset",
    type: "options",
    options: PRESET_OPTIONS,
  },
  { key: "mainBgColor", label: "Background", type: "color" },

  // --- Suggestion Pills group ---
  {
    key: "pillLayout",
    label: "Layout",
    type: "options",
    group: "Suggestion Pills",
    options: [
      { label: "Vertical", value: "vertical" },
      { label: "Horizontal", value: "horizontal" },
    ],
  },
  {
    key: "pillShowIcon",
    label: "Show Icon",
    type: "toggle",
    group: "Suggestion Pills",
  },
  {
    key: "pillBorderRadius",
    label: "Border Radius",
    type: "number",
    min: 0,
    max: 100,
    step: 2,
    group: "Suggestion Pills",
  },
  {
    key: "pillBorderColor",
    label: "Border Color",
    type: "color",
    group: "Suggestion Pills",
  },
  {
    key: "pillBorderWidth",
    label: "Border Width",
    type: "number",
    min: 0,
    max: 3,
    step: 0.5,
    group: "Suggestion Pills",
  },
  {
    key: "pillBgColor",
    label: "Background",
    type: "color",
    group: "Suggestion Pills",
  },
  {
    key: "pillTextColor",
    label: "Text Color",
    type: "color",
    group: "Suggestion Pills",
  },
  {
    key: "pillShadowEnabled",
    label: "Shadow",
    type: "toggle",
    group: "Suggestion Pills",
  },
  {
    key: "pillShadow",
    label: "Shadow Value (CSS)",
    type: "text",
    group: "Suggestion Pills",
  },

  // --- Input Box group ---
  {
    key: "inputBorderColor",
    label: "Border Color",
    type: "color",
    group: "Input Box",
  },
  {
    key: "inputBorderWidth",
    label: "Border Width",
    type: "number",
    min: 0,
    max: 5,
    step: 0.5,
    group: "Input Box",
  },
  {
    key: "inputBorderRadius",
    label: "Border Radius",
    type: "number",
    min: 0,
    max: 100,
    step: 2,
    group: "Input Box",
  },
  {
    key: "inputMinHeight",
    label: "Min Height (0=auto)",
    type: "number",
    min: 0,
    max: 200,
    step: 10,
    group: "Input Box",
  },
  {
    key: "inputShadowEnabled",
    label: "Shadow",
    type: "toggle",
    group: "Input Box",
  },
  {
    key: "inputShadow",
    label: "Shadow Value (CSS)",
    type: "text",
    group: "Input Box",
  },
];

// ============================================================================
// Default state (matches "vertical-expanded" preset per Figma mock)
// ============================================================================

const defaultState: ConciergePanelWidgetState = {
  preset: "vertical-expanded",
  mainBgColor: themeColor("transparent"),

  // Pill defaults (vertical-expanded) — border 1px #F3F3F3, radius 100px, shadow per mock
  pillLayout: "vertical",
  pillShowIcon: false,
  pillBorderRadius: 100,
  pillBorderColor: customColor("#F3F3F3"),
  pillBorderWidth: 1,
  pillBgColor: themeColor("secondary"),
  pillTextColor: themeColor("textPrimary"),
  pillShadowEnabled: true,
  pillShadow: "0 4px 16px 0 rgba(0, 0, 0, 0.05)",
  pillContainerBgColor: themeColor("transparent"),
  pillContainerBorderRadius: 12,

  // Input defaults (vertical-expanded: taller input 116px, rounded rect)
  inputBorderColor: customColor("#F3F3F3"),
  inputBorderWidth: 1,
  inputBorderRadius: 20,
  inputMinHeight: 116,
  inputShadowEnabled: false,
  inputShadow: "8px 8px 14px 0 rgba(0, 0, 0, 0.03)",
};

// ============================================================================
// Field-mapping tables: panel field keys → child widget keys
// ============================================================================

const PILL_FIELD_MAP: Record<string, string> = {
  pillLayout: "layout",
  pillShowIcon: "showIcon",
  pillBorderRadius: "pillBorderRadius",
  pillBorderColor: "pillBorderColor",
  pillBorderWidth: "pillBorderWidth",
  pillBgColor: "pillBgColor",
  pillTextColor: "pillTextColor",
  pillShadowEnabled: "pillShadowEnabled",
  pillShadow: "pillShadow",
  pillContainerBgColor: "containerBgColor",
  pillContainerBorderRadius: "containerBorderRadius",
};

const INPUT_FIELD_MAP: Record<string, string> = {
  inputBorderColor: "borderColor",
  inputBorderWidth: "borderWidth",
  inputBorderRadius: "borderRadiusSingleLine",
  inputMinHeight: "minHeight",
  inputShadowEnabled: "shadowEnabled",
  inputShadow: "shadow",
};

// ============================================================================
// Component
// ============================================================================

export const ConciergePanelWidget: React.FC<ConciergePanelWidgetProps> = ({
  widgetId = "concierge-panel",
  displayName = "Concierge Panel",
  initialState = {},
  suggestionListWidgetId = "concierge-suggestion-list",
  chatInputWidgetId = "concierge-chat-input",
  suppressChildEdits = true,
  children,
}) => {
  const {
    state,
    isLoading,
    isSaving,
    handleEditChange: rawHandleEditChange,
    handleSave,
    themeColors,
    themeFonts,
    resolveColor,
    canSave,
  } = useWidgetEditable<ConciergePanelWidgetState>(
    widgetId,
    { ...defaultState, ...initialState },
    editFields
  );

  // Hooks to push state to child widgets
  const { updateWidgetConfig: updateSuggestionList, getWidgetState: getSuggestionListState } = useWidgetEdit(
    suggestionListWidgetId
  );
  const { updateWidgetConfig: updateChatInput, getWidgetState: getChatInputState } =
    useWidgetEdit(chatInputWidgetId);

  // Once on mount (after load), push preset defaults to child widgets — but only
  // for keys the child doesn't already have saved, so user customizations survive.
  const hasSyncedPresetToChildrenRef = useRef(false);
  useEffect(() => {
    if (isLoading || state.preset === "custom" || hasSyncedPresetToChildrenRef.current) return;
    const config = presetConfigs[state.preset as Exclude<ConciergePanelPreset, "custom">];
    if (config) {
      hasSyncedPresetToChildrenRef.current = true;
      const savedSuggestionList = getSuggestionListState() || {};
      Object.entries(config.suggestionList).forEach(([k, v]) => {
        if (!(k in savedSuggestionList)) {
          updateSuggestionList(k, v);
        }
      });
      const savedChatInput = getChatInputState() || {};
      Object.entries(config.chatInput).forEach(([k, v]) => {
        if (!(k in savedChatInput)) {
          updateChatInput(k, v);
        }
      });
    }
  }, [isLoading, state.preset, updateSuggestionList, updateChatInput, getSuggestionListState, getChatInputState]);

  // Mapping from panel field keys to child widget keys
  const PILL_FIELD_MAP: Record<string, string> = {
    pillLayout: "layout",
    pillShowIcon: "showIcon",
    pillBorderRadius: "pillBorderRadius",
    pillBorderColor: "pillBorderColor",
    pillBorderWidth: "pillBorderWidth",
    pillBgColor: "pillBgColor",
    pillTextColor: "pillTextColor",
    pillShadowEnabled: "pillShadowEnabled",
    pillShadow: "pillShadow",
    pillContainerBgColor: "containerBgColor",
    pillContainerBorderRadius: "containerBorderRadius",
  };

  const INPUT_FIELD_MAP: Record<string, string> = {
    inputBorderColor: "borderColor",
    inputBorderWidth: "borderWidth",
    inputBorderRadius: "borderRadiusSingleLine",
    inputMinHeight: "minHeight",
    inputShadowEnabled: "shadowEnabled",
    inputShadow: "shadow",
  };

  // Wrap handleEditChange to push preset/individual values to child widgets
  const handleEditChange = useCallback(
    (key: string, value: any) => {
      // Preset selection: apply all values at once
      if (key === "preset" && value !== "custom") {
        const presetKey = value as Exclude<ConciergePanelPreset, "custom">;
        const config = presetConfigs[presetKey];
        if (config) {
          rawHandleEditChange("preset", value);
          if (config.panel) {
            Object.entries(config.panel).forEach(([k, v]) => {
              rawHandleEditChange(k, v);
            });
          }
          Object.entries(config.suggestionList).forEach(([k, v]) => {
            updateSuggestionList(k, v);
          });
          Object.entries(config.chatInput).forEach(([k, v]) => {
            updateChatInput(k, v);
          });
          return;
        }
      }

      // Individual pill field: push to SuggestionListWidget
      if (key in PILL_FIELD_MAP) {
        rawHandleEditChange("preset", "custom");
        rawHandleEditChange(key, value);
        updateSuggestionList(PILL_FIELD_MAP[key], value);
        return;
      }

      // Individual input field: push to ChatInput
      if (key in INPUT_FIELD_MAP) {
        rawHandleEditChange("preset", "custom");
        rawHandleEditChange(key, value);
        updateChatInput(INPUT_FIELD_MAP[key], value);
        return;
      }

      rawHandleEditChange(key, value);
    },
    [rawHandleEditChange, updateSuggestionList, updateChatInput]
  );

  if (isLoading) {
    return <></>;
  }

  const renderedChildren = children(state, resolveColor);
  const widgetContent = suppressChildEdits ? (
    <WidgetEditSuppressProvider>{renderedChildren}</WidgetEditSuppressProvider>
  ) : (
    renderedChildren
  );

  return (
    <WidgetWrapper
      widgetId={widgetId}
      displayName={displayName}
      editFields={editFields}
      editValues={state}
      onEditChange={handleEditChange}
      onSave={canSave ? handleSave : undefined}
      isSaving={isSaving}
      themeColors={themeColors}
      themeFonts={themeFonts}
      containerClassName="widget-fill-height"
    >
      {widgetContent}
    </WidgetWrapper>
  );
};
